import React from 'react';
import User from '@users/user/User';
import { useSessionContext } from '@users/session/SessionContext';
import { useDataroomTenantContext } from '@dataroom/tenant/DataroomTenantContext';
import { useDataroomTwoFactorAuthenticationContext } from '@dataroom/authentication/DataroomTwoFactorAuthenticationContext';
import useAlertsModal from '../Modals/AlertsModal/useAlertsModal';
import EmailAlertsTextContent from '@dataroom/email-alerts/ui/EmailAlertsTextContent';
import { IDataroomListItem } from '../../../domain/vo/DataroomListItem';
import { IFileUploadEmailAlerts } from '@dataroom/email-alerts/domain/vo/FileUploadEmailAlerts';
import { IStagingFileUploadEmailAlerts } from '@dataroom/email-alerts/domain/vo/StagingFileUploadEmailAlerts';
import { IDigestEmailAlerts } from '@dataroom/email-alerts/domain/vo/DigestEmailAlerts';
import { IAccessRequestEmailAlerts } from '@dataroom/email-alerts/domain/vo/AccessRequestEmailAlerts';
import { IQuestionsEmailAlerts } from '@dataroom/email-alerts/domain/vo/QuestionsEmailAlerts';
import styles from './dataroomEmailAlerts.module.scss';


interface IProps {
  cellProps: {
    row: IDataroomListItem,
  },
}

const DataroomEmailAlerts = ({ cellProps }: IProps) => {
  const { tenant } = useDataroomTenantContext();
  const { setModalData, isSessionTwoFactorVerified, userEmail } = useDataroomTwoFactorAuthenticationContext();
  const { getSession } = useSessionContext();

  const {
    row,
    row: {
      notificationSettings,
      timezone,
      twoFactor,
      id,
      allowedActions,
      qnaNotificationSettings,
    },
  } = cellProps;

  const {
    canAccessStaging,
    canEditDigestAlerts,
    canEditAccessRequestAlerts,
    canEditQnaAlerts,
  } = allowedActions;

  const {
    realtime, daily, weekly,
    stagingRealtime, stagingDaily, stagingWeekly,
    digestDaily, digestWeekly,
    accessRequestRealtime, accessRequestDaily, accessRequestWeekly,
  } = notificationSettings![0];

  const {
    AlertsModal,
    dataroom,
    showAlertsModal,
    hideAlertsModal,
  } = useAlertsModal();

  const onEmailAlertsClick = async () => {
    if (!User.isSupportUserByTenant(tenant, userEmail!) && !isSessionTwoFactorVerified && twoFactor) {
      const session = await getSession({ clearCache: true });
      const visitedDatarooms = session![tenant]?.activeSessions || [];
      const isDataroomVisited = visitedDatarooms.indexOf(id) !== -1;
      if (session!.two_factor || isDataroomVisited) {
        showAlertsModal(row);
        return;
      }
      setModalData({
        isModalOpen: true,
        entityName: row.displayName,
        onSuccessCallback: () => showAlertsModal(row),
      });
      return;
    }
    showAlertsModal(row);
  };

  const fileUploadAlerts = {
    realtime,
    daily,
    weekly,
  } as IFileUploadEmailAlerts;

  const stagingFileUploadAlerts = canAccessStaging ? {
    realtime: stagingRealtime,
    daily: stagingDaily,
    weekly: stagingWeekly,
  } as IStagingFileUploadEmailAlerts : null;

  const digestAlerts = canEditDigestAlerts ? {
    daily: digestDaily,
    weekly: digestWeekly,
  } as IDigestEmailAlerts : null;

  const accessRequestAlerts = canEditAccessRequestAlerts ? {
    realtime: accessRequestRealtime,
    daily: accessRequestDaily,
    weekly: accessRequestWeekly,
  } as IAccessRequestEmailAlerts : null;

  return (
    <>
      <EmailAlertsTextContent
        timezone={ timezone }
        fileUploadAlerts={ fileUploadAlerts }
        stagingFileUploadAlerts={ stagingFileUploadAlerts as IStagingFileUploadEmailAlerts }
        digestAlerts={ digestAlerts as IDigestEmailAlerts }
        accessRequestAlerts={ accessRequestAlerts as IAccessRequestEmailAlerts }
        qnaAlerts={ (canEditQnaAlerts ? qnaNotificationSettings : null) as IQuestionsEmailAlerts }
        showAlertsModal={ onEmailAlertsClick }
        wrapperClassName={ styles.emailAlertsWrp }
        emailAlertsContainerClassName={ styles.emailAlertsTextContainer }
        dataTest="emailAlertsOpenModalButton"
      />
      { dataroom && (
        <AlertsModal
          dataroom={ dataroom }
          closeModal={ hideAlertsModal }
        />
      ) }
    </>
  );
};

export default DataroomEmailAlerts;
